import { log } from '@/utils/logger';

/**
 * Request metrics collector for tracking performance and request details
 * Provides unified logging and metrics collection for HTTP requests
 */
export class RequestMetrics {
  private startTime: number;
  private readonly isServerSide: boolean;
  private readonly requestSource: string;
  private readonly url: string;
  private readonly method: string;
  private requestOptions?: RequestInit;

  constructor(url: string, method: string) {
    this.isServerSide = typeof window === 'undefined';
    this.requestSource = this.isServerSide ? 'BE' : 'FE';
    this.startTime = performance.now();
    this.url = url;
    this.method = method;
  }

  /**
   * Store request options for later use in the final log
   */
  setRequestOptions(options: RequestInit) {
    this.requestOptions = options;
  }

  /**
   * Log comprehensive request information with response data and metrics
   * Only logs requests that take over 500ms to avoid noise
   */
  logEnd(response?: Response, json?: { [key: string]: unknown }, error?: Error) {
    if (!this.isServerSide) return; // Skip logging on client-side to avoid cluttering console

    const duration = performance.now() - this.startTime;
    const durationMs = Number(duration.toFixed(2));

    // Only log requests that take over 1500ms
    if (durationMs <= 1500 && !error) return;

    const status = response?.status;
    const success = !error && response?.ok;

    // Extract request information
    const headers = this.requestOptions?.headers as Record<string, string>;
    const bodySize = typeof this.requestOptions?.body === 'string' ? this.requestOptions.body.length : 0;
    const hasSignal = !!this.requestOptions?.signal;
    const additionalOptions = this.requestOptions
      ? Object.keys(this.requestOptions).filter(key => !['method', 'headers', 'signal', 'body'].includes(key)).length
      : 0;

    // Extract X-Cache-Status header if present
    const cacheStatus = response?.headers?.get('X-Cache-Status');

    const metrics = {
      url: this.url,
      method: this.method,
      source: this.requestSource,
      duration: durationMs,
      timestamp: new Date().toISOString(),
      headersCount: Object.keys(headers || {}).length,
      ...(bodySize && { bodySize }),
      hasSignal,
      hasAdditionalOptions: additionalOptions > 0,
      ...(status && { status }),
      ...(json && { responseSize: JSON.stringify(json).length }),
      ...(error && { error: error.message }),
      ...(this.isServerSide && { serverSideMetrics: true }),
      ...(cacheStatus && { cacheStatus }),
    };

    const eventType = success ? 'completed' : 'failed';
    const statusText = error ? `failed: ${error.message}` : status ? `(${status})` : 'completed';
    log.info({
      event: `${this.method} request ${eventType} in ${durationMs}ms`,
      message: `${this.requestSource} ${this.method} request to ${this.url} ${statusText} in ${durationMs}ms`,
      stringyfy: metrics,
    });
  }
}
